import React, { useCallback, useEffect, useRef, useState } from "react";
import { map, mergeRight } from "ramda";
import { useDeletePapiServer, usePapiServers } from "../papiServerServices";

import PanelLoadingIndicator from "components/PanelLoadingIndicator";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import { makeStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import { useDispatch, useSelector } from "react-redux";
import { FetchError, fetchSecurityUser } from "util/fetchUtils";
import { logOut } from "auth/authActions";

import AddIcon from "@material-ui/icons/Add";
import EditPapiServer from "security-settings/papi-servers/components/EditPapiServer";
import ConfirmDeleteDialog from "security-settings/papi-servers/components/ConfirmDeleteDialog";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import { notBlank } from "../../../util/ext";
import { Container, Paper } from "@material-ui/core";
import PapiServerListItem from "./PapiServerListItem";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    panel: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listPaper: {
      width: "100%",
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: 60,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      padding: theme.spacing(4),
    },

    leftContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 200,
    },

    rightContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexGrow: 1,
    },

    title: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    newButton: {
      textTransform: "uppercase",
      color: "#127DA9",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },

    list: {
      height: "100%",
    },

    listItem: {
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: grey[300],
    },

    creatorLogo: {
      width: "100%",
      height: "100%",
    },
  }));

const PapiServerBrowse = () => {
  const [scrollVisible, setScrollVisible] = useState(true);

  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();

  const [servers, setServers] = useState([]);
  const [serversLoaded, setServersLoaded] = useState(false);
  const [userCache, setUserCache] = useState(new Map());
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [currentServer, setCurrentServer] = useState({});
  const [errorAlert, setErrorAlert] = useState({ open: false });
  const classes = useStyles(scrollVisible)();

  const handleEditDialogOnCancel = () => {
    setEditDialogVisible(false);
  };

  const handleEditDialogOnSave = server => {
    setCurrentServer(server);
    setEditDialogVisible(false);
  };

  const handleAddServerOnClick = () => {
    setCurrentServer({});
    setEditDialogVisible(true);
  };

  const handleEditServerOnClick = server => {
    setCurrentServer(server);
    setEditDialogVisible(true);
  };

  const handleDeleteServerOnClick = server => {
    setCurrentServer(server);
    setDeleteConfirmVisible(true);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const handleError = useCallback(
    message => err => {
      if (err instanceof FetchError) {
        if (err.status === 401) {
          dispatch(logOut());
        } else {
          showAlert(message);
        }
      } else {
        showAlert(err.message);
      }
    },
    [dispatch],
  );

  const deletePapiServer = useDeletePapiServer();
  const handleDeleteServerOk = async server => {
    try {
      setDeleteConfirmVisible(false);
      await deletePapiServer.mutateAsync({
        data: { patronApiServerId: server.patronApiServerId },
        resourceIds: {
          id: server.patronApiServerId,
        },
      });
    } catch (err) {
      handleError("Error Deleting Patron API Server. Server my be in use.")(
        err,
      );
    }
  };

  const lookupUser = useCallback(
    async userId => {
      if (!userCache.has(userId)) {
        const user = await fetchSecurityUser(auth)(userId);
        setUserCache(uc => uc.set(userId, user));
      }

      return userCache.get(userId);
    },
    [auth, userCache],
  );

  const papiServers = usePapiServers();
  useEffect(() => {
    const loadPapiServers = async servers => {
      try {
        const serverFull = await Promise.all(
          map(async srv => {
            const [createdBy, updatedBy] = await Promise.all([
              lookupUser(srv.createdBy),
              lookupUser(srv.updatedBy),
            ]);
            srv.createdUser = createdBy;
            srv.updatedUser = updatedBy;
            return srv;
          }, servers),
        );

        setServers(serverFull);
        setServersLoaded(true);
      } catch (err) {
        handleError("Error fetching Patron API Servers.")(err);
      }
    };

    if (papiServers.isSuccess) {
      loadPapiServers(papiServers.data);
      setScrollVisible(true);
    }
  }, [auth, lookupUser, handleError, papiServers.isSuccess, papiServers.data]);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(tableContainerEl.current)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      console.log(`Scroll Visible : ${scrollVisible}`);
      setScrollVisible(scrollVisible);
    }
  }, [tableContainerEl]);

  return (
    <Container
      id="papi-server-browse"
      maxWidth={false}
      classes={{ root: classes.panel }}
    >
      <EditPapiServer
        open={editDialogVisible}
        server={currentServer}
        onSave={handleEditDialogOnSave}
        onClose={handleEditDialogOnCancel}
      />

      <ConfirmDeleteDialog
        open={deleteConfirmVisible}
        server={currentServer}
        onCancel={() => setDeleteConfirmVisible(false)}
        onOk={handleDeleteServerOk}
      />

      <div>
        <div>
          <div className={classes.toolbar}>
            <div className={classes.leftContainer}>
              <div className={classes.title}>Patron API Servers</div>
            </div>

            <div>
              <div>
                <Button
                  variant={"text"}
                  startIcon={<AddIcon />}
                  classes={{ root: classes.newButton }}
                  onClick={handleAddServerOnClick}
                >
                  New Patron API Server
                </Button>
              </div>
            </div>
          </div>
        </div>

        {serversLoaded ? (
          <Container maxWidth={false} classes={{ root: classes.panel }}>
            <Paper elevation={0} className={classes.listPaper}>
              <Container
                maxWidth={false}
                ref={tableContainerEl}
                className={classes.listContainer}
              >
                <List
                  classes={{
                    root: classes.list,
                  }}
                >
                  {servers.map(server => {
                    const selected =
                      server.patronApiServerId ===
                        currentServer.patronApiServerId ||
                      server.hostname === currentServer.hostname;
                    return (
                      <PapiServerListItem
                        key={server.patronApiServerId}
                        server={server}
                        selected={selected}
                        onEditClick={handleEditServerOnClick}
                        onDeleteClick={handleDeleteServerOnClick}
                      />
                    );
                  })}
                </List>
              </Container>
            </Paper>
          </Container>
        ) : (
          <PanelLoadingIndicator />
        )}
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PapiServerBrowse;
